/**
 * Du texte dont les fragments mathématiques sont rendus, pas affichés bruts.
 *
 * Mesuré le 20 août 2026 : sur 631 énoncés de mathématiques en base, **aucun** ne
 * portait de LaTeX, et l'enfant lisait `x^2` et `1/2` en texte brut — juste
 * au-dessus du champ MathLive où elle tape sa réponse, qui est le composant que
 * `CLAUDE.md` désigne comme le plus important de l'application. Taper une fraction
 * y est soigné ; lire une puissance ne l'était pas.
 *
 * **Le fragment est délimité par celui qui écrit l'énoncé**, entre `\(` et `\)`.
 * C'est la seule façon de ne pas deviner : un composant qui chercherait les
 * formules tout seul transformerait le `1/2` de « une heure et demie » en
 * fraction, et se tromperait en silence. Les consignes de génération
 * (`components/exercise/data/consignes/`) portent la règle ; ce composant ne fait
 * que l'honorer.
 *
 * **Un énoncé sans délimiteur s'affiche tel quel**, ce qui est le cas des 631
 * déjà en base. Ils ne se dégradent pas : ils restent ce qu'ils étaient, et la
 * profondeur du pool les diluera.
 */

import { useMemo } from "react";
import katex from "katex";

import { rendreMarkdown, separerFormules } from "@/ui/enonceMarkdown";

/** `\(…\)`, non gourmand, sur plusieurs lignes. */
const FRAGMENT = /\\\((.+?)\\\)/gs;

type Morceau = { math: false; texte: string } | { math: true; html: string };

/** Découper une chaîne en morceaux de texte et de formules rendues. */
export function decouper(source: string): Morceau[] {
  const morceaux: Morceau[] = [];
  let curseur = 0;
  for (const trouve of source.matchAll(FRAGMENT)) {
    const debut = trouve.index ?? 0;
    if (debut > curseur) {
      morceaux.push({ math: false, texte: source.slice(curseur, debut) });
    }
    // Le groupe capturant est garanti par le motif, mais TypeScript ne le sait pas
    // et il a raison de demander : un motif modifié plus tard pourrait le rendre
    // optionnel sans que rien ne le signale.
    const formule = trouve[1] ?? "";
    let html: string;
    try {
      html = katex.renderToString(formule, { throwOnError: true, displayMode: false });
    } catch {
      // **Une formule illisible se montre, elle ne disparaît pas.** Un rendu qui
      // avale son entrée laisserait un trou dans l'énoncé, et l'enfant chercherait
      // ce qui manque au lieu de répondre.
      morceaux.push({ math: false, texte: trouve[0] });
      curseur = debut + trouve[0].length;
      continue;
    }
    morceaux.push({ math: true, html });
    curseur = debut + trouve[0].length;
  }
  if (curseur < source.length) {
    morceaux.push({ math: false, texte: source.slice(curseur) });
  }
  return morceaux;
}

/**
 * Rendre une formule, ou la rendre **visible telle quelle** si KaTeX la refuse.
 *
 * Extrait de `decouper` pour être partagé avec le chemin Markdown : les deux
 * doivent traiter une formule illisible de la même manière, et deux copies de
 * cette règle finiraient par diverger.
 */
function formuleEnHtml(fragment: string): { math: true; html: string } | { math: false; texte: string } {
  const dedans = /^\\\((.*)\\\)$/s.exec(fragment);
  try {
    return {
      math: true,
      html: katex.renderToString(dedans?.[1] ?? fragment, {
        throwOnError: true,
        displayMode: false,
      }),
    };
  } catch {
    // **Une formule illisible se montre, elle ne disparaît pas.** Un rendu qui
    // avale son entrée laisserait un trou dans l'énoncé, et l'enfant chercherait
    // ce qui manque au lieu de répondre.
    return { math: false, texte: fragment };
  }
}

export function TexteMathematique({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  // **Le Markdown d'abord, les formules ensuite.** L'ordre n'est pas libre : le
  // découpage Markdown met les formules de côté pour ne pas lire la barre d'une
  // valeur absolue `\(|x|\)` comme une cellule de tableau. Rendre les formules
  // en premier produirait du HTML KaTeX que l'analyse Markdown traverserait
  // ensuite, avec ses `|` et ses `-`.
  const blocs = useMemo(() => {
    const { html, formules } = rendreMarkdown(children ?? "");
    return separerFormules(html).map((m) =>
      "indice" in m ? formuleEnHtml(formules[m.indice] ?? "") : m,
    );
  }, [children]);

  return (
    <div className={className}>
      {blocs.map((b, i) =>
        // Le HTML vient soit de KaTeX, soit de `rendreMarkdown`, qui échappe tout
        // ce qui n'appartient pas à son sous-ensemble. Aucun contenu tiers.
        "html" in b ? (
          <span key={i} dangerouslySetInnerHTML={{ __html: b.html }} />
        ) : (
          <span key={i}>{b.texte}</span>
        ),
      )}
    </div>
  );
}
